import type { BillingHealthLevel, BillingVsRevenue, PlanBilling } from './metricsTypes';

export type SubscriptionVerdict = 'current' | 'overdue' | 'watch' | 'overload' | 'no_record';

export interface SubscriptionStoryInput {
  billing?: PlanBilling | null;
  vs?: BillingVsRevenue | null;
  canPlan: boolean;
  canBurden: boolean;
}

export interface SubscriptionStory {
  verdict: SubscriptionVerdict;
  hasRecord: boolean;
  planName: string | null;
  paymentMethod: string | null;
  planAmount: number;
  debt: number;
  alDia: boolean;
  revenue: number | null;
  burden: number | null;
  coversDebt: boolean | null;
  nivel: BillingHealthLevel | null;
  showBurden: boolean;
  showDebt: boolean;
}

export interface SubscriptionRiskRow {
  hasRecord: boolean;
  alDia: boolean;
  deudaAcumulada: number;
  nivel?: BillingHealthLevel | null;
}

export function buildSubscriptionStory(input: SubscriptionStoryInput): SubscriptionStory {
  const billing = input.billing ?? null;
  const vs = input.vs ?? null;
  const hasRecord = Boolean(billing?.hasRecord || vs?.hasBillingRecord);
  const alDia = vs?.alDia ?? billing?.alDia ?? false;
  const debt = vs?.deudaAcumulada ?? billing?.deudaAcumulada ?? 0;
  const nivel = vs?.nivel ?? null;

  let verdict: SubscriptionVerdict = 'no_record';
  if (hasRecord) {
    if (!alDia || debt > 0) {
      verdict = 'overdue';
    } else if (nivel === 'rojo') {
      verdict = 'overload';
    } else if (nivel === 'amarillo') {
      verdict = 'watch';
    } else {
      verdict = 'current';
    }
  }

  return {
    verdict,
    hasRecord,
    planName: vs?.planNombre ?? billing?.planNombre ?? null,
    paymentMethod: billing?.medioPago ?? null,
    planAmount: vs?.planMonto ?? billing?.monto ?? 0,
    debt,
    alDia,
    revenue: input.canBurden && vs ? vs.ingresos : null,
    burden: input.canBurden ? (vs?.cargaPlanSobreIngresos ?? null) : null,
    coversDebt: input.canBurden && vs && debt > 0 ? vs.cubreDeuda : null,
    nivel,
    showBurden: input.canBurden,
    showDebt: input.canPlan || input.canBurden,
  };
}

/** Chip de dashboard: solo deuda o carga en riesgo. Silencio si está al día y saludable. */
export function shouldShowSubscriptionRiskChip(rows: SubscriptionRiskRow[]): boolean {
  return rows.some((row) => {
    if (!row.hasRecord) {
      return false;
    }
    if (!row.alDia || row.deudaAcumulada > 0) {
      return true;
    }
    return row.nivel === 'rojo' || row.nivel === 'amarillo';
  });
}
